import { create } from 'zustand';

export type ToastTone = 'neutral' | 'good' | 'bad';

export interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
  /** Optional single action, e.g. "Undo". */
  action?: { label: string; run: () => void };
}

interface ToastState {
  toasts: Toast[];
  push(message: string, options?: { tone?: ToastTone; action?: Toast['action']; ttlMs?: number }): string;
  dismiss(id: string): void;
}

let counter = 0;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (message, options = {}) => {
    const id = `t${++counter}`;
    const toast: Toast = { id, message, tone: options.tone ?? 'neutral', action: options.action };
    set((state) => ({ toasts: [...state.toasts, toast].slice(-4) }));
    const ttl = options.ttlMs ?? 4200;
    if (ttl > 0) {
      window.setTimeout(() => get().dismiss(id), ttl);
    }
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative helper for non-component code (mutations, sockets). */
export const toast = {
  show: (message: string, tone: ToastTone = 'neutral') => useToastStore.getState().push(message, { tone }),
  good: (message: string) => useToastStore.getState().push(message, { tone: 'good' }),
  bad: (message: string) => useToastStore.getState().push(message, { tone: 'bad' }),
};
